import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ScoresTable - displays all screening points for the current grade level.
 *
 * Layout: one section per screener with task badges.
 * Shows max 5 groups by default; "Show all" expands to full list.
 *
 * Props:
 *   screeners - array of { name, date, tasks: [{ name, score_pct, standard_score, category }] }
 *   showScores - whether assessment results should be rendered
 */
const DEFAULT_VISIBLE_GROUPS = 5;
const CATEGORY_EXPAND_ANIMATION_MS = 240;
const INPLACE_PANEL_ANIMATION_MS = 220;
const INPLACE_PANEL_HOVER_DELAY_MS = 180;

const PINNED_GROUP_PATTERNS = [/letter.?sound/i];

function isPinnedGroup(name) {
  return PINNED_GROUP_PATTERNS.some((pattern) => pattern.test(name || ""));
}

function buildCanonicalGroupOrder(rawScreeners) {
  const frequencyByNormName = new Map();
  const originalNameByNormName = new Map();

  rawScreeners.forEach((screener) => {
    const namesInScreener = new Set();
    screener.groups.forEach((group) => {
      const norm = (group.name || "").toLowerCase();
      namesInScreener.add(norm);
      if (!originalNameByNormName.has(norm)) {
        originalNameByNormName.set(norm, group.name || "");
      }
    });
    namesInScreener.forEach((norm) => {
      frequencyByNormName.set(norm, (frequencyByNormName.get(norm) || 0) + 1);
    });
  });

  const maxFreq = rawScreeners.length;

  const sortedNormNames = Array.from(originalNameByNormName.keys()).sort(
    (a, b) => {
      const aFreq = frequencyByNormName.get(a) || 0;
      const bFreq = frequencyByNormName.get(b) || 0;

      // Only pin Letter Sound when it appears in every screener
      const aPinned = isPinnedGroup(a) && aFreq === maxFreq;
      const bPinned = isPinnedGroup(b) && bFreq === maxFreq;
      if (aPinned !== bPinned) return aPinned ? -1 : 1;

      if (aFreq !== bFreq) return bFreq - aFreq;

      return 0;
    }
  );

  const orderMap = new Map();
  sortedNormNames.forEach((norm, index) => orderMap.set(norm, index));
  return orderMap;
}

function sortGroupsByCanonicalOrder(groups, canonicalOrder) {
  return [...groups].sort((a, b) => {
    const aOrder =
      canonicalOrder.get((a.name || "").toLowerCase()) ?? Infinity;
    const bOrder =
      canonicalOrder.get((b.name || "").toLowerCase()) ?? Infinity;
    return aOrder - bOrder;
  });
}

function ScoresTable({
  screeners,
  showScores = true,
  forceExpandAll = false,
  heading = "Assessment results the parent report is based on",
}) {
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);
  const [inplaceGroupId, setInplaceGroupId] = useState(null);
  const screenersWithGroups = useMemo(() => {
    const rawScreeners = (screeners || []).map((screener, index) => {
      const screenerKey = `${screener.name || "screener"}:${
        screener.date || index
      }:${index}`;
      return {
        ...screener,
        key: screenerKey,
        groups: groupTasksByCategory(screener.tasks || [], screenerKey),
      };
    });

    const canonicalOrder = buildCanonicalGroupOrder(rawScreeners);
    return rawScreeners.map((screener) => ({
      ...screener,
      groups: sortGroupsByCanonicalOrder(screener.groups, canonicalOrder),
    }));
  }, [screeners]);
  const expandableGroupIds = screenersWithGroups.flatMap((screener) =>
    screener.groups
      .filter((group) => isExpandableCategory(group))
      .map((group) => group.id)
  );
  const hasExpandableGroups = expandableGroupIds.length > 0;
  const effectiveExpandedGroupIds = forceExpandAll ? expandableGroupIds : expandedGroupIds;
  const allExpandableGroupsOpen =
    hasExpandableGroups &&
    expandableGroupIds.every((groupId) => effectiveExpandedGroupIds.includes(groupId));

  if (!screeners || screeners.length === 0) {
    return null;
  }

  if (!showScores) return null;

  const toggleInplaceGroup = (groupId) => {
    setInplaceGroupId((currentGroupId) =>
      currentGroupId === groupId ? null : groupId
    );
  };

  const openInplaceGroup = (groupId) => {
    setInplaceGroupId(groupId);
  };

  const toggleAllCategories = () => {
    setInplaceGroupId(null);
    setExpandedGroupIds(allExpandableGroupsOpen ? [] : expandableGroupIds);
  };

  return (
    <div className="eoy-scores-table-wrap">
      <div className="eoy-scores-table-heading-row">
        <div className="eoy-scores-table-header">
          {heading}
        </div>
        {hasExpandableGroups && !forceExpandAll && (
          <button
            type="button"
            className="eoy-expand-all-categories-btn no-print"
            onClick={toggleAllCategories}
          >
            {allExpandableGroupsOpen
              ? "Collapse all categories"
              : "Expand all categories"}
          </button>
        )}
      </div>
      {screenersWithGroups.map((screener) => (
        <ScreenerSection
          key={screener.key}
          screener={screener}
          expandedGroupIds={effectiveExpandedGroupIds}
          inplaceGroupId={inplaceGroupId}
          onToggleGroup={toggleInplaceGroup}
          onOpenGroup={openInplaceGroup}
          onCloseInplacePanel={() => setInplaceGroupId(null)}
        />
      ))}
    </div>
  );
}

function ScreenerSection({
  screener,
  expandedGroupIds,
  inplaceGroupId,
  onToggleGroup,
  onOpenGroup,
  onCloseInplacePanel,
}) {
  const [showAllGroups, setShowAllGroups] = useState(false);
  const groups = screener.groups || [];
  const hasMore = groups.length > DEFAULT_VISIBLE_GROUPS;
  const visibleGroups = showAllGroups
    ? groups
    : groups.slice(0, DEFAULT_VISIBLE_GROUPS);
  const visibleGroupRows = chunkGroups(visibleGroups, DEFAULT_VISIBLE_GROUPS);

  return (
    <div className="eoy-screener-section eoy-screener-section-open">
      <div className="eoy-screener-collapse eoy-screener-collapse-open">
        <div className="eoy-screener-collapse-inner">
          <div className="eoy-screener-name-row">
            <span className="eoy-screener-title">{screener.name}</span>
            <span className="eoy-screener-date">{screener.date}</span>
          </div>
          <div className="eoy-task-groups">
            {visibleGroupRows.map((rowGroups, rowIndex) => (
              <div
                className="eoy-task-group-row"
                key={rowGroups[0]?.id || rowIndex}
              >
                {rowGroups.map((group) => (
                  <ScoreGroup
                    key={group.id}
                    group={group}
                    expanded={expandedGroupIds.includes(group.id)}
                    inplaceOpen={inplaceGroupId === group.id}
                    onToggle={() => onToggleGroup(group.id)}
                    onOpen={() => onOpenGroup(group.id)}
                    onCloseInplacePanel={onCloseInplacePanel}
                  />
                ))}
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              className="eoy-expand-btn"
              onClick={() => setShowAllGroups(!showAllGroups)}
            >
              {showAllGroups
                ? "Show fewer"
                : `Show all ${groups.length} score groups`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function chunkGroups(groups, rowSize) {
  const rows = [];

  for (let index = 0; index < groups.length; index += rowSize) {
    rows.push(groups.slice(index, index + rowSize));
  }

  return rows;
}

function ScoreGroup({
  group,
  expanded,
  inplaceOpen,
  onToggle,
  onOpen,
  onCloseInplacePanel,
}) {
  const isExpandable = isExpandableCategory(group);
  const labelId = `eoy-score-group-${group.id}`;
  const inplacePanelId = `${labelId}-inplace-panel`;
  const groupRef = useRef(null);
  const hoverTimerRef = useRef(null);

  useEffect(() => {
    if (!inplaceOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (groupRef.current?.contains(event.target)) return;
      onCloseInplacePanel();
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [inplaceOpen, onCloseInplacePanel]);

  useEffect(
    () => () => {
      clearTimeout(hoverTimerRef.current);
    },
    []
  );

  const openOnHover = () => {
    if (!isExpandable || expanded || inplaceOpen) return;

    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(onOpen, INPLACE_PANEL_HOVER_DELAY_MS);
  };

  const closeOnHoverEnd = () => {
    clearTimeout(hoverTimerRef.current);

    if (inplaceOpen) {
      onCloseInplacePanel();
    }
  };

  return (
    <div
      className="eoy-score-group"
      ref={groupRef}
      onMouseEnter={openOnHover}
      onMouseLeave={closeOnHoverEnd}
    >
      <div className="eoy-score-group-summary">
        {isExpandable && !expanded ? (
          <button
            type="button"
            className={`eoy-task-badge-wrap eoy-score-group-toggle ${
              inplaceOpen ? "eoy-score-group-toggle-open" : ""
            }`}
            aria-label={group.name}
            aria-expanded={inplaceOpen}
            aria-controls={inplaceOpen ? inplacePanelId : undefined}
            onClick={onToggle}
          >
            <span className="eoy-task-label">{group.name}</span>
            <span className="eoy-score-group-score-row">
              <span
                className={`eoy-scores-table-badge ${getScoreClass(
                  group.score_pct
                )}`}
                data-tooltip={getInterpretation(group.score_pct)}
              >
                {group.score_pct}%
              </span>
              <span className="eoy-score-group-plus" aria-hidden="true">
                {inplaceOpen ? "-" : "+"}
              </span>
            </span>
          </button>
        ) : (
          <div className="eoy-task-label">{group.name}</div>
        )}
        {(!isExpandable || expanded) && (
          <span
            className={`eoy-scores-table-badge ${getScoreClass(
              group.score_pct
            )}`}
            data-tooltip={getInterpretation(group.score_pct)}
          >
            {group.score_pct}%
          </span>
        )}
      </div>
      {isExpandable && (
        <CategoryTaskCollapse
          id={labelId}
          open={expanded}
          tasks={group.tasks}
        />
      )}
      {isExpandable && !expanded && (
        <CategoryInplacePanel
          group={group}
          id={inplacePanelId}
          open={inplaceOpen}
          onClose={onCloseInplacePanel}
        />
      )}
    </div>
  );
}

function CategoryInplacePanel({ group, id, open, onClose }) {
  const [shouldRender, isOpen] = useAnimatedOpenState(open, INPLACE_PANEL_ANIMATION_MS);

  if (!shouldRender) return null;

  return (
    <div
      className={`eoy-score-group-inplace-panel no-print ${
        isOpen ? "eoy-score-group-inplace-panel-open" : ""
      }`}
      id={id}
      aria-hidden={!open}
    >
      <button
        type="button"
        className="eoy-score-group-inplace-summary"
        aria-label={group.name}
        aria-expanded={open}
        aria-controls={id}
        onClick={onClose}
      >
        <span className="eoy-task-label">{group.name}</span>
        <span
          className={`eoy-scores-table-badge ${getScoreClass(
            group.score_pct
          )}`}
          data-tooltip={getInterpretation(group.score_pct)}
        >
          {group.score_pct}%
        </span>
      </button>
      <div className="eoy-score-group-inplace-tasks">
        {group.tasks.map((task) => (
          <TaskBadge key={task.name} task={task} />
        ))}
      </div>
    </div>
  );
}

function CategoryTaskCollapse({ id, open, tasks }) {
  const [shouldRender, isOpen] = useAnimatedOpenState(open, CATEGORY_EXPAND_ANIMATION_MS);

  if (!shouldRender) return null;

  return (
    <div
      className={`eoy-category-task-collapse ${
        isOpen ? "eoy-category-task-collapse-open" : ""
      }`}
      id={id}
      aria-hidden={!open}
    >
      <div className="eoy-category-task-collapse-inner">
        <div className="eoy-category-task-badges">
          {tasks.map((task) => (
            <TaskBadge key={task.name} task={task} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TaskBadge({ task }) {
  return (
    <div className="eoy-task-badge-wrap eoy-category-task-badge-wrap">
      <div className="eoy-task-label">{task.name}</div>
      <span
        className={`eoy-scores-table-badge ${getScoreClass(task.score_pct)}`}
        data-tooltip={getInterpretation(task.score_pct)}
      >
        {task.score_pct}%
      </span>
    </div>
  );
}

function isExpandableCategory(group) {
  return group.isCategory && group.tasks.length > 1;
}

export function groupTasksByCategory(tasks, idPrefix = "") {
  const groups = [];
  const groupedTasksByCategoryId = new Map();

  tasks.forEach((task, index) => {
    const categoryId = getTaskCategoryId(task);
    const categoryName = getTaskCategoryName(task);

    if (!categoryId) {
      groups.push({
        id: `${idPrefix}:task:${task?.name || index}:${index}`,
        isCategory: false,
        name: task?.name || "Untitled task",
        score_pct: normalizeScore(task?.score_pct),
        tasks: [task],
      });
      return;
    }

    if (!groupedTasksByCategoryId.has(categoryId)) {
      const group = {
        id: `${idPrefix}:category:${categoryId}`,
        isCategory: true,
        name: categoryName || "Untitled category",
        score_pct: 0,
        tasks: [],
      };
      groupedTasksByCategoryId.set(categoryId, group);
      groups.push(group);
    }

    groupedTasksByCategoryId.get(categoryId).tasks.push(task);
  });

  return groups.map((group) => ({
    ...group,
    score_pct:
      group.tasks.length === 1
        ? normalizeScore(group.tasks[0]?.score_pct)
        : getAverageScore(group.tasks),
  }));
}

function getTaskCategoryId(task) {
  return task?.category?.id || task?.parentTaskid || task?.parentTaskId;
}

function getTaskCategoryName(task) {
  return task?.category?.name || task?.parentTaskName;
}

function getAverageScore(tasks) {
  const scores = tasks
    .map((task) => Number(task?.score_pct))
    .filter((score) => Number.isFinite(score));

  if (scores.length === 0) return 0;

  return Math.round(
    scores.reduce((total, score) => total + score, 0) / scores.length
  );
}

function normalizeScore(score) {
  const numericScore = Number(score);
  return Number.isFinite(numericScore) ? Math.round(numericScore) : 0;
}

function useAnimatedOpenState(open, duration) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    let animationFrameId;
    let openFrameId;
    let closeTimerId;

    if (open) {
      setShouldRender(true);
      animationFrameId = requestAnimationFrame(() => {
        openFrameId = requestAnimationFrame(() => setIsOpen(true));
      });
    } else {
      setIsOpen(false);
      closeTimerId = setTimeout(() => setShouldRender(false), duration);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      cancelAnimationFrame(openFrameId);
      clearTimeout(closeTimerId);
    };
  }, [open, duration]);

  return [shouldRender, isOpen];
}

/**
 * Returns CSS class for colour-coding.
 */
function getScoreClass(score) {
  if (score >= 0 && score <= 20) return "eoy-badge-red";
  if (score > 20 && score <= 40) return "eoy-badge-amber";
  if (score > 40 && score <= 80) return "eoy-badge-green";
  if (score > 80) return "eoy-badge-darkgreen";
  return "";
}

/**
 * Returns interpretation label for tooltip on hover.
 */
function getInterpretation(score) {
  if (score >= 0 && score <= 20) return "Emerging";
  if (score > 20 && score <= 40) return "Fair Performance";
  if (score > 40 && score <= 60) return "Good Performance";
  if (score > 60 && score <= 80) return "Very Good Performance";
  if (score > 80 && score <= 100) return "Excellent Performance";
  return "";
}

export default ScoresTable;
